import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import Navbar from './components/common/Navbar';
import Home from './pages/Home';
import Profile from './pages/Profile';
import VerifySuccess from './components/auth/VerifySuccess';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import Terms from './pages/Terms';
import NotFound from './pages/NotFound';
import './styles/global.css';
import { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from './styles/themes';
import GlobalStyles from './styles/GlobalStyles';
import { ThemeContext } from './contexts/ThemeContext';
import { UniversityProvider } from './contexts/UniversityContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ScrollToTop from './components/common/ScrollToTop';

function AppContent() {
  return (
    <Router>
      <ScrollToTop />
      <Helmet>
        <title>Sapotix - Red social universitaria</title>
        <meta name="description" content="La red social anónima para estudiantes universitarios. Comparte tus pensamientos con otros estudiantes de tu universidad." />
        <meta name="keywords" content="sapotix, red social, universidad, estudiantes, anónimo" />
      </Helmet>
      
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />}>
          <Route path="hashtag/:hashtag" element={null} />
        </Route>
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route path="/profile/:username" element={<Profile />} />
        <Route path="/verify-success" element={<VerifySuccess />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

function App() {
  // Inicializar el estado con el valor de localStorage o la preferencia del sistema
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Escuchar cambios en la preferencia de tema del sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Solo cambiar si el usuario no ha establecido una preferencia
      if (!localStorage.getItem('theme')) {
        setDarkMode(e.matches);
      }
    };
    
    // Añadir listener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback para navegadores más antiguos
      mediaQuery.addListener(handleChange);
    }
    
    // Limpiar listener
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Guardar preferencia en localStorage cuando cambia
  useEffect(() => {
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    
    // Aplicar clase al body para estilos globales
    if (darkMode) {
      document.body.classList.add('dark-mode');
      document.documentElement.classList.add('dark-mode');
      document.documentElement.style.backgroundColor = '#18191a';
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.style.backgroundColor = '#f0f2f5';
    }
  }, [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
        <GlobalStyles />
        <UniversityProvider>
          <AuthProvider>
            <WebSocketProvider>
              <AppContent />
            </WebSocketProvider>
          </AuthProvider>
        </UniversityProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;